import React, { useState, useEffect } from 'react';
import { verifyPastoralAccess } from '../../api/config';
import BulletinManager from './BulletinManager';
import GivingDashboard from './GivingDashboard';
import PrayerRequestsManager from './PrayerRequestsManager';

const PastorsTools = () => {
    const [activeTab, setActiveTab] = useState('bulletins');
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [bethelUser, setBethelUser] = useState(null);
    const [formsUser, setFormsUser] = useState(null);

    useEffect(() => {
        checkPastoralAccess();
    }, []);

    const checkPastoralAccess = async () => {
        try {
            setLoading(true);
            setError('');

            // Get current user from forms app (Google OAuth)
            const storedUser = localStorage.getItem('user');
            if (!storedUser) {
                setError('Please log in to the forms app first.');
                setLoading(false);
                return;
            }

            const formsUserData = JSON.parse(storedUser);
            setFormsUser(formsUserData);

            if (!formsUserData.email) {
                setError('No email found in your profile. Please contact an administrator.');
                setLoading(false);
                return;
            }

            // Verify pastoral access using email
            const verification = await verifyPastoralAccess(formsUserData.email);
            
            if (verification.error) {
                setError(`Verification failed: ${verification.error}`);
                setLoading(false);
                return;
            }

            if (verification.hasAccess) {
                setHasAccess(true);
                setBethelUser(verification.user);
            } else {
                setError(`Access denied. Your email (${formsUserData.email}) is not authorized for Pastor's Tools. Please contact an administrator if you believe this is an error.`);
            }
        } catch (err) {
            console.error('Pastoral access check failed:', err);
            setError('Failed to verify access. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Verifying pastoral access...</p>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h2>
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}
                
                <div className="text-gray-700 space-y-3">
                    <p><strong>Pastor's Tools</strong> is restricted to authorized pastoral team members.</p>
                    
                    {formsUser && (
                        <div className="bg-gray-50 p-3 rounded">
                            <p className="text-sm text-gray-600">Currently logged in as:</p>
                            <p className="font-medium">{formsUser.name} ({formsUser.email})</p>
                        </div>
                    )}
                    
                    <p className="text-sm">
                        If you should have access, please contact an administrator to ensure your email 
                        is added to the pastoral team in the Bethel app system.
                    </p>
                </div>
                
                <button
                    onClick={checkPastoralAccess}
                    className="mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Retry Verification
                </button>
            </div>
        );
    }

    const tabs = [
        { id: 'bulletins', label: 'Weekly Bulletins', icon: '📄' },
        { id: 'giving', label: 'Weekly Giving', icon: '💰' },
        { id: 'prayers', label: 'Prayer Requests', icon: '🙏' }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Pastor's Tools</h1>
                    {bethelUser && (
                        <p className="text-sm text-gray-600 mt-1">
                            Welcome, {bethelUser.firstName} {bethelUser.lastName}
                        </p>
                    )}
                </div>
                <div className="text-right">
                    <p className="text-sm text-gray-600">Authenticated via</p>
                    <p className="text-sm font-medium">{formsUser?.email}</p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                {activeTab === 'bulletins' && <BulletinManager />}
                {activeTab === 'giving' && <GivingDashboard />}
                {activeTab === 'prayers' && <PrayerRequestsManager />}
            </div>
        </div>
    );
};

export default PastorsTools;